import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/contexts/OrganizationContext';
import { toast } from '@/hooks/useToast';
import type { TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

export type VisibilityLevel = 'members' | 'admins' | 'private';

export interface Timesheet {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  column_config: Record<string, boolean>;
  column_order: string[];
  project_id: string | null;
  business_id: string | null;
  person_id: string | null;
  visibility: VisibilityLevel;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface TimeEntry {
  id: string;
  timesheet_id: string;
  organization_id: string;
  project_id: string | null;
  person_id: string | null;
  business_id: string | null;
  task: string | null;
  entry_date: string;
  start_time: string;
  end_time: string | null; // NULL indicates an active (clocked in) entry
  is_estimated: boolean;
  is_billable: boolean;
  note: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  // Calculated field (not in DB)
  hours?: number;
}

export interface CreateTimesheetInput {
  name: string;
  description?: string;
  column_config?: Record<string, boolean>;
  column_order?: string[];
  project_id?: string | null;
  business_id?: string | null;
  person_id?: string | null;
  visibility?: VisibilityLevel;
}

export interface UpdateTimesheetInput {
  id: string;
  name?: string;
  description?: string;
  column_config?: Record<string, boolean>;
  column_order?: string[];
  project_id?: string | null;
  business_id?: string | null;
  person_id?: string | null;
  visibility?: VisibilityLevel;
}

/**
 * Hook to fetch all timesheets for the current organization
 */
export function useTimesheets() {
  const { selectedOrganization } = useOrganization();

  return useQuery({
    queryKey: ['timesheets', selectedOrganization?.id],
    queryFn: async () => {
      if (!selectedOrganization?.id) return [];

      const { data, error } = await supabase
        .from('timesheets')
        .select('*')
        .eq('organization_id', selectedOrganization.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Timesheet[];
    },
    enabled: !!selectedOrganization?.id,
  });
}

/**
 * Hook to fetch a single timesheet by ID
 */
export function useTimesheet(timesheetId: string | undefined) {
  return useQuery({
    queryKey: ['timesheet', timesheetId],
    queryFn: async () => {
      if (!timesheetId) return null;

      const { data, error } = await supabase
        .from('timesheets')
        .select('*')
        .eq('id', timesheetId)
        .single();

      if (error) throw error;
      return data as Timesheet;
    },
    enabled: !!timesheetId,
  });
}

/**
 * Hook to fetch time entries for a timesheet
 */
export function useTimeEntries(timesheetId: string | undefined) {
  return useQuery({
    queryKey: ['time_entries', timesheetId],
    queryFn: async () => {
      if (!timesheetId) return [];

      const { data, error } = await supabase
        .from('time_entries')
        .select(`
          *,
          project:projects(id, name, code),
          person:people(id, first_name, last_name),
          business:businesses(id, name, code)
        `)
        .eq('timesheet_id', timesheetId)
        .order('entry_date', { ascending: false })
        .order('start_time', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Calculate hours for each entry
      return (data || []).map(entry => ({
        ...entry,
        hours: calculateHours(entry.start_time, entry.end_time),
      }));
    },
    enabled: !!timesheetId,
  });
}

/**
 * Calculate hours between two time strings.
 *
 * Exported so other modules (e.g. `lib/timesheetSummary.ts`) can compute
 * entry hours from raw `time_entries` rows without re-implementing the
 * midnight-crossing logic.
 */
export function calculateHours(startTime: string | null, endTime: string | null): number | null {
  if (!startTime || !endTime) return null;

  const parseTime = (time: string) => {
    const match = time.match(/^(\d{1,2}):(\d{2})/);
    if (!match) return null;
    return { hours: parseInt(match[1], 10), minutes: parseInt(match[2], 10) };
  };

  const start = parseTime(startTime);
  const end = parseTime(endTime);

  if (!start || !end) return null;

  const startMinutes = start.hours * 60 + start.minutes;
  let endMinutes = end.hours * 60 + end.minutes;

  // Handle crossing midnight
  if (endMinutes < startMinutes) {
    endMinutes += 24 * 60;
  }

  return Math.round((endMinutes - startMinutes) / 60 * 100) / 100;
}

/**
 * Hook to create a new timesheet
 */
export function useCreateTimesheet() {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationFn: async (input: CreateTimesheetInput) => {
      if (!selectedOrganization?.id) throw new Error('No organization selected');

      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      const insertData: TablesInsert<'timesheets'> = {
        name: input.name,
        description: input.description || null,
        column_config: input.column_config || {
          is_estimated: true,
          is_billable: true,
          project_id: true,
          person_id: true,
          business_id: true,
          entry_date: true,
          start_time: true,
          end_time: true,
          note: true,
        },
        column_order: input.column_order || [
          'is_estimated', 'is_billable', 'project_id', 'person_id',
          'business_id', 'entry_date', 'start_time', 'end_time', 'hours', 'note'
        ],
        project_id: input.project_id || null,
        business_id: input.business_id || null,
        person_id: input.person_id || null,
        visibility: input.visibility || 'members',
        organization_id: selectedOrganization.id,
        created_by: user.user.id,
      };

      const { data, error } = await supabase
        .from('timesheets')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      return data as Timesheet;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
      toast.successTemplates.created('Timesheet');
    },
    onError: () => {
      toast.errorTemplates.create('timesheet');
    },
  });
}

/**
 * Hook to update a timesheet
 */
export function useUpdateTimesheet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateTimesheetInput) => {
      const updateData: TablesUpdate<'timesheets'> = {};
      if (input.name !== undefined) updateData.name = input.name;
      if (input.description !== undefined) updateData.description = input.description;
      if (input.column_config !== undefined) updateData.column_config = input.column_config;
      if (input.column_order !== undefined) updateData.column_order = input.column_order;
      if (input.project_id !== undefined) updateData.project_id = input.project_id;
      if (input.business_id !== undefined) updateData.business_id = input.business_id;
      if (input.person_id !== undefined) updateData.person_id = input.person_id;
      if (input.visibility !== undefined) updateData.visibility = input.visibility;

      const { data, error } = await supabase
        .from('timesheets')
        .update(updateData)
        .eq('id', input.id)
        .select()
        .single();

      if (error) throw error;
      return data as Timesheet;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
      queryClient.invalidateQueries({ queryKey: ['timesheet', data.id] });
      toast.successTemplates.updated('Timesheet');
    },
    onError: () => {
      toast.errorTemplates.update('timesheet');
    },
  });
}

/**
 * Hook to delete a timesheet
 */
export function useDeleteTimesheet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (timesheetId: string) => {
      const { error } = await supabase
        .from('timesheets')
        .delete()
        .eq('id', timesheetId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
      toast.successTemplates.deleted('Timesheet');
    },
    onError: () => {
      toast.errorTemplates.delete('timesheet');
    },
  });
}

/**
 * Hook to save time entries (bulk create/update/delete)
 */
export interface BatchSaveResult {
  created: TimeEntry[];
  updated: TimeEntry[];
  deleted_count: number;
}

const TIME_ENTRY_FIELDS = [
  'project_id', 'person_id', 'business_id', 'task',
  'entry_date', 'start_time', 'end_time',
  'is_estimated', 'is_billable', 'note',
] as const;

const UUID_FIELDS = new Set(['project_id', 'person_id', 'business_id']);

function pickTimeEntryFields(data: Partial<TimeEntry>): Partial<TimeEntry> {
  const result: Record<string, unknown> = {};
  for (const key of TIME_ENTRY_FIELDS) {
    if (key in data) {
      // Convert empty strings to null for UUID fields
      result[key] = UUID_FIELDS.has(key) && data[key] === '' ? null : data[key];
    }
  }
  return result as Partial<TimeEntry>;
}

export function useSaveTimeEntries() {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({ timesheetId, entries }: {
      timesheetId: string;
      entries: Array<{
        type: 'create' | 'update' | 'delete';
        rowId?: string;
        data: Partial<TimeEntry>;
      }>;
    }): Promise<BatchSaveResult> => {
      if (!selectedOrganization?.id) throw new Error('No organization selected');

      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      const creates = entries
        .filter(e => e.type === 'create')
        .map(e => pickTimeEntryFields(e.data));
      const updates = entries
        .filter(e => e.type === 'update' && e.rowId)
        .map(e => ({ id: e.rowId, ...pickTimeEntryFields(e.data) }));
      const deleteIds = entries
        .filter(e => e.type === 'delete' && e.rowId)
        .map(e => e.rowId!);

      const { data, error } = await supabase.rpc('batch_save_time_entries', {
        p_timesheet_id: timesheetId,
        p_organization_id: selectedOrganization.id,
        p_user_id: user.user.id,
        p_creates: creates,
        p_updates: updates,
        p_delete_ids: deleteIds,
      });

      if (error) throw error;
      return data as unknown as BatchSaveResult;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['time_entries', variables.timesheetId] });
      queryClient.invalidateQueries({ queryKey: ['todays-entries'] });
      queryClient.invalidateQueries({ queryKey: ['active-time-entry'] });
      // Saved entries move `hours_this_month` / `hours_last_month` /
      // `last_entry_date`, all of which the timesheet INDEX projects — so the
      // list has to be invalidated under the prefix `useTimesheetIndex` is
      // keyed under (`['timesheets', orgId, 'index', {}]`).
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
      queryClient.invalidateQueries({ queryKey: ['timesheet-summary'] });
      toast.success('Time entries saved');
    },
    onError: () => {
      toast.error('Failed to save time entries');
    },
  });
}
